import {
  CoverageCalculator,
  CoverageCalculatorOptions,
  CustomerInfo,
  ItemInfo,
} from ".";
import { DynamicReturnTypes, InternationalReturnPricing } from "../team";
import {
  calculateDynamicReturnsPrice,
  getPricingRulesInLocalCurrency,
  listsHaveMatchingElement,
} from "./calculator-utils";
import {
  getReturnsCoverageExclusions,
  itemValidForReturns,
} from "./check-product";
import {
  getAllReturnEligibleCountries,
  getInternationalPrice,
} from "./country-check";

const isProductEnabled = (
  team: CoverageCalculatorOptions,
  ignoreMerchantPaidForEnablementCheck?: boolean,
) => {
  return !!(
    team.settings.enabled &&
    team.settings.returns?.enabled &&
    team.settings.coverageEnabled &&
    (ignoreMerchantPaidForEnablementCheck
      ? true
      : team.settings.paidModel === "customer_paid") &&
    (team.pricePerOrder || team.settings.returns.dynamicPricing)
  );
};

const isShopperEligible = (
  customerInfo: CustomerInfo,
  team: CoverageCalculatorOptions,
) => {
  if (
    listsHaveMatchingElement(
      customerInfo.customerTags,
      team.settings.coverageExcludeCustomerTags,
    )
  ) {
    return false;
  }

  const returnCountries = getAllReturnEligibleCountries(
    team.settings.countries,
    team.settings.returns?.internationalPricing,
  );
  const ValidCountry =
    returnCountries.includes(customerInfo.country) ||
    returnCountries.length == 0;

  if (!ValidCountry) {
    return false;
  }

  if (
    customerInfo.isBusinessAccount &&
    team.settings.shopifyExtension?.disableBusinessAccountInStorefront
  ) {
    return false;
  }

  if (customerInfo.usingZapietPickup) {
    return false;
  }

  return true;
};

const isItemEligible = (item: ItemInfo, team: CoverageCalculatorOptions) => {
  if (item.vendor === "re:do") {
    return false;
  }

  if (item.isFinalSale) {
    return false;
  }

  if (item.requiresShipping === false) {
    return false;
  }

  if (item.price && team.settings.coverageExcludeCentEnding.length) {
    const cents = item.price.toFixed(2).split(".")[1];
    const centExclusions = team.settings.coverageExcludeCentEnding;
    const exclusionMatch = centExclusions.find(
      (centExclusion) => cents === centExclusion,
    );

    if (exclusionMatch) {
      return false;
    }
  }

  // This handles the case where the discount only has a name and not a code
  const hasExcludedDiscount = item.discounts.some((code) =>
    team.settings.discountCodePartialMatch
      ? team.settings.coverageExcludeDiscountCodes.some((exclude) =>
          (code.code || code.name).includes(exclude),
        )
      : team.settings.coverageExcludeDiscountCodes.includes(
          code.code || code.name,
        ),
  );
  if (hasExcludedDiscount) {
    return false;
  }

  const coverageExclusions = getReturnsCoverageExclusions({
    returnsCoverage: {
      coverageExcludeProperties: team.settings.coverageExcludeProperties,
      coverageExcludeCollections: team.settings.coverageExcludeCollections,
      invalidProductTags: team.settings.coverageExcludeProductTags,
    },
    finalSaleReturnsCoverage: {
      coverageEnabled: !!team.settings.returns?.finalSaleReturns?.enabled,
      validProductTags:
        team.settings.returns?.finalSaleReturns?.validProductTags ?? [],
      validCollectionTitles:
        team.settings.returns?.finalSaleReturns?.validCollectionTitles ?? [],
    },
  });

  const isItemValid = itemValidForReturns({
    ...coverageExclusions,
    itemInfo: item,
  });
  if (!isItemValid) {
    return false;
  }

  return true;
};

const calculateCoveragePrice = (
  team: CoverageCalculatorOptions,
  country: string | undefined,
  numProducts: number,
  amount: number,
  exchangeRate?: number,
): number => {
  const dynamicPricing = team.settings.returns?.dynamicPricing;

  if (exchangeRate) {
    if (
      dynamicPricing &&
      dynamicPricing.type === DynamicReturnTypes.ITEM_COUNT
    ) {
      dynamicPricing.maxPrice = dynamicPricing.maxPrice * exchangeRate;
      dynamicPricing.pricePerAdditonalItem =
        dynamicPricing.pricePerAdditonalItem * exchangeRate;
    } else if (
      dynamicPricing &&
      dynamicPricing.type === DynamicReturnTypes.CART_VALUE
    ) {
      dynamicPricing.maxPrice = dynamicPricing.maxPrice * exchangeRate;
      dynamicPricing.pricingRuleSet = getPricingRulesInLocalCurrency(
        [dynamicPricing.pricingRuleSet],
        exchangeRate,
      )[0];
    }

    if (team.pricePerOrder) {
      team.pricePerOrder = (
        parseFloat(team.pricePerOrder) * exchangeRate
      ).toString();
    }
  }

  const basePrice = parseFloat(team.pricePerOrder || "0");
  if (isNaN(basePrice) || !basePrice) {
    return 0;
  }
  let coveragePrice = basePrice;

  if (dynamicPricing) {
    coveragePrice = calculateDynamicReturnsPrice(
      dynamicPricing,
      basePrice,
      numProducts,
      amount,
    );
  }

  if (team.settings.returns?.internationalPricing && country) {
    coveragePrice = getInternationalPrice(
      coveragePrice,
      country,
      team.settings.countries,
      team.settings.returns.internationalPricing as InternationalReturnPricing,
    );
  }

  return coveragePrice;
};

const isCartEligible = (
  numValidItems: number,
  numInvalidItems: number,
  cartTotal: number,
  team: CoverageCalculatorOptions,
) => {
  if (team.settings.excludeCoverageIfAnyInvalidProduct && numInvalidItems > 0) {
    return false;
  }

  return numValidItems > 0;
};

// returns a price if coverage is valid. otherwise returns undefined
const getCoverage = (
  team: CoverageCalculatorOptions,
  customerInfo: CustomerInfo,
  items: ItemInfo[],
  conversionRate?: number,
  ignoreMerchantPaidForEnablementCheck?: boolean,
): { eligible: boolean; price?: number } => {
  if (!isProductEnabled(team, ignoreMerchantPaidForEnablementCheck)) {
    return { eligible: false, price: undefined };
  }
  if (!isShopperEligible(customerInfo, team)) {
    return { eligible: false, price: undefined };
  }

  let totalPrice = 0;
  let numValidItems = 0;
  let numInvalidItems = 0;

  for (const item of items) {
    const itemValid = isItemEligible(item, team);
    if (itemValid) {
      totalPrice += item.price * item.quantity;
      numValidItems += item.quantity;
    } else {
      numInvalidItems += item.quantity;
    }
  }

  if (!isCartEligible(numValidItems, numInvalidItems, totalPrice, team)) {
    return { eligible: false, price: undefined };
  }

  const price = calculateCoveragePrice(
    team,
    customerInfo.country,
    numValidItems,
    totalPrice,
    conversionRate,
  );

  return { eligible: true, price };
};

export const returnCoverageCalculator: CoverageCalculator = {
  type: "Returns",
  isProductEnabled,
  isShopperEligible,
  isItemEligible,
  isCartEligible,
  // need to handle non usd here.
  calculateCoveragePrice,
  getCoverage,
};
